/**
 * Admin event workspace.
 *
 * The most complex page in the app: a phased accordion (Quoting → Job Prep →
 * Payments) over a single booking, with section auto-open logic, inline
 * autosave, AI quote / job-summary generation, the date-change regear banner,
 * substitution + manual-override markers, and the gear shortfall banner.
 *
 * The data fetch + every mutation handler lives in the useEventWorkspace
 * hook; all workspace endpoints are live in go-service under /api/admin/* —
 * see event-workspace-api.ts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { deriveWorkspaceView } from "../lib/booking-phase";
import { useDirtySections } from "../lib/use-dirty-sections";
import { computeWorkspaceStaleness } from "../lib/summary-staleness";
import {
  MUTATION_KEYS,
  useEventWorkspace,
  type WorkspaceEventData,
} from "../lib/use-event-workspace";
import { Button } from "../components/ui/Button";
import { Banner } from "../components/ui/Banner";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { EventHeader } from "../components/admin/EventHeader";
import { EventInfoCards } from "../components/admin/EventInfoCards";
import { HiredInBanner } from "../components/admin/HiredInBanner";
import { CallProposalsSection } from "../components/admin/CallProposalsSection";
import { GearListSection } from "../components/admin/GearListSection";
import {
  getAllocationConflicts,
  NO_ALLOCATION_CONFLICTS,
  type AllocationConflicts,
} from "../lib/allocation-api";
import { QuoteSection } from "../components/admin/QuoteSection";
import { JobDetailsSection } from "../components/admin/JobDetailsSection";
import { CrewSection } from "../components/admin/CrewSection";
import { VehiclePlanBlock } from "../components/admin/VehiclePlanBlock";
import { useVehicleDayTimes } from "../lib/use-vehicle-day-times";
import { JobSummarySection } from "../components/admin/JobSummarySection";
import { ClientPaymentSection } from "../components/admin/ClientPaymentSection";
import { ContractorPaymentSection } from "../components/admin/ContractorPaymentSection";
import {
  WorkspacePhaseTabs,
  type PhaseTabKey,
} from "../components/admin/WorkspacePhaseTabs";
import {
  parseSectionParam,
  type SectionKey,
  type SectionRefs,
} from "../components/admin/workspace-sections";
import { isActive } from "../lib/assignment-status";
import { formatDateTime, getEventDayCount } from "../lib/format-utils";
import { getCompanySettings } from "../lib/company-settings-api";
import { cx } from "@shared/cx";
import page from "./page.module.css";
import styles from "./event-workspace.module.css";

type Assignment = WorkspaceEventData["assignments"][number];
type AssignmentWithContractor = Assignment & {
  contractor: NonNullable<Assignment["contractor"]>;
};

const PHASE_TAB_KEYS: PhaseTabKey[] = ["quoting", "jobPrep", "payments"];

/** `?tab=jobPrep` → the tab, so a workspace stage is linkable and survives Back. */
function parseTabParam(v: string | null): PhaseTabKey | null {
  return PHASE_TAB_KEYS.find((k) => k === v) ?? null;
}

/** Which phase tab a booking lands on by default, given its lifecycle step. */
function defaultPhaseTab(step: number): PhaseTabKey {
  if (step <= 1) return "quoting";
  if (step <= 4) return "jobPrep";
  return "payments";
}

/** Which phase tab owns a given accordion section. */
function phaseTabForSection(section: SectionKey): PhaseTabKey {
  switch (section) {
    case "gearList":
    case "quote":
      return "quoting";
    case "jobDetails":
    case "jobSummary":
    case "crew":
      return "jobPrep";
    case "clientPayment":
    case "contractorPayment":
      return "payments";
  }
}

export default function EventWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // useParams types `id` as string | undefined; the route is `/events/:id`
  // so it is always present. Fall back to "" defensively.
  const ws = useEventWorkspace(id ?? "");

  // The open accordion section and the stage it belongs to are seeded from
  // `?section=` once, at mount — a deep link opens a section; toggling one
  // afterwards is not a place.
  const [openSection, setOpenSection] = useState<SectionKey | null>(() =>
    parseSectionParam(searchParams.get("section")),
  );
  const [scrollTarget, setScrollTarget] = useState<SectionKey | null>(
    openSection,
  );

  // Company quote defaults (#23): deposit % and quote-valid days used when a
  // booking has no approval values yet. Best-effort — falls back to no
  // deposit and 28 days while loading or on error.
  const [quoteDefaults, setQuoteDefaults] = useState({
    depositPercent: "0",
    validDays: "28",
  });
  useEffect(() => {
    const ac = new AbortController();
    getCompanySettings(ac.signal)
      .then((s) =>
        setQuoteDefaults({
          depositPercent: String(s.deposit_percent_default),
          validDays: String(s.quote_valid_days_default),
        }),
      )
      .catch(() => {
        // keep 50 / 28 fallbacks
      });
    return () => ac.abort();
  }, []);

  // Allocation conflicts (#305): which of this Booking's Vehicles and
  // Contractors another Booking already holds on these dates. Read once here
  // and handed to both surfaces that show it — the transport rows in the gear
  // editor and the crew rows — so the two can never disagree.
  //
  // Refetched whenever the workspace read changes, which is exactly the set
  // of moves that change the answer: a vehicle selection saves and refetches,
  // crew saves and refetches, and a date change patches and refetches. It is
  // a read; it never gates a save.
  const [allocationConflicts, setAllocationConflicts] =
    useState<AllocationConflicts>(NO_ALLOCATION_CONFLICTS);
  useEffect(() => {
    if (!ws.data) return;
    const ac = new AbortController();
    getAllocationConflicts(id ?? "", ac.signal)
      .then(setAllocationConflicts)
      .catch(() => {
        // A missing warning is the honest degradation — never a reason to
        // break the workspace.
        setAllocationConflicts(NO_ALLOCATION_CONFLICTS);
      });
    return () => ac.abort();
  }, [id, ws.data]);

  // The fleet's out/back pair per day. Held here, not in either consumer: the
  // day schedule edits it and the Vehicles block below invalidates it, and
  // both live inside Job Details.
  const vehicleDayTimes = useVehicleDayTimes(id ?? "");

  // Per-section dirty flags published by the mounted contributing sections
  // (GearList, JobDetails). Staleness = project(snapshot) !== project(last
  // read) OR a contributing section is dirty; the flag spans until the
  // post-save refetch lands. See summary-staleness.ts.
  const dirtySections = useDirtySections();

  // Live (unsaved) gear rows published by GearListSection so the HiredInBanner
  // reflects a freshly-added hired-in item immediately, before save+refetch.
  // null = editor hasn't emitted yet → fall back to saved server gearItems.
  // Reset on every refetch so server data wins until the editor re-emits.
  const [liveGearItems, setLiveGearItems] = useState<
    | {
        item_name: string;
        is_hired_in?: boolean;
        suppliers?: {
          name: string;
          email?: string | null;
          phone?: string | null;
        }[];
      }[]
    | null
  >(null);
  const handleGearItemsChange = useCallback(
    (items: NonNullable<typeof liveGearItems>) => setLiveGearItems(items),
    [],
  );

  // A gear PUT was refused with 409 (#242 C2): another operator's tab
  // replaced booking_items since this editor was seeded, so its local state
  // is known-stale and there is nothing to merge. Bumping this key remounts
  // the editor onto the winning read. It is NOT the deleted transport
  // wipe-guard protocol (#243 sweep 2 D4) — transport rows are server-owned
  // and re-seed themselves from every read, with nothing for a second file
  // to remember. Both pieces are workspace-owned so the notice survives the
  // remount it triggers.
  const [gearConflictKey, setGearConflictKey] = useState(0);
  const [gearConflictNotice, setGearConflictNotice] = useState(false);

  const gearListRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLDivElement>(null);
  const jobDetailsRef = useRef<HTMLDivElement>(null);
  const jobSummaryRef = useRef<HTMLDivElement>(null);
  const crewRef = useRef<HTMLDivElement>(null);
  const clientPaymentRef = useRef<HTMLDivElement>(null);
  const contractorPaymentRef = useRef<HTMLDivElement>(null);

  const sectionRefs = useMemo<SectionRefs>(
    () => ({
      gearList: gearListRef,
      quote: quoteRef,
      jobDetails: jobDetailsRef,
      jobSummary: jobSummaryRef,
      crew: crewRef,
      clientPayment: clientPaymentRef,
      contractorPayment: contractorPaymentRef,
    }),
    [],
  );

  // The single SPA-side answer to "what state is this booking in": phase,
  // per-section state, per-artifact staleness. Everything below (default
  // section, phase tabs, section props) reads this — nothing recomputes it.
  const view = useMemo(() => {
    if (!ws.data) return null;
    const clientStale = computeWorkspaceStaleness(ws.data, dirtySections.dirty);
    return deriveWorkspaceView({
      booking: ws.data.booking,
      approval: ws.data.approval,
      assignments: ws.data.assignments.map((a) => ({
        status: a.status,
        payment_status: a.paymentStatus,
      })),
      hasLineItems: ws.data.quoteLineItems.length > 0,
      depositStatus: ws.data.paymentSummary.depositStatus,
      balanceStatus: ws.data.paymentSummary.balanceStatus,
      depositAmount: ws.data.paymentSummary.depositAmount,
      clientStale,
    });
  }, [ws.data, dirtySections.dirty]);

  useEffect(() => {
    document.title = "Event Workspace — Rental Admin";
  }, []);

  // The stage is the URL, not a copy of it: `?tab=` if set, else the stage a
  // deep-linked section belongs to, else the one this Booking's phase is up
  // to.
  const currentTab: PhaseTabKey =
    parseTabParam(searchParams.get("tab")) ??
    (openSection ? phaseTabForSection(openSection) : null) ??
    defaultPhaseTab(view?.step ?? 0);

  // `replace` — moving between stages of one booking is not history. The
  // section param goes with it: the stage the operator just left owns it.
  const selectTab = useCallback(
    (tab: PhaseTabKey): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          next.delete("section");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Finishing a stage moves the Operator to the next one: when the Booking's
  // phase crosses into another stage's range (the quote is approved, the
  // event happens), the workspace follows it. Only a *change* advances —
  // landing on a mid-life Booking and clicking back to Quoting stays put
  // until the phase moves again.
  const phaseTab = view ? defaultPhaseTab(view.step) : null;
  const lastPhaseTab = useRef<PhaseTabKey | null>(null);
  useEffect(() => {
    if (!phaseTab) return;
    const prev = lastPhaseTab.current;
    lastPhaseTab.current = phaseTab;
    if (prev === null || prev === phaseTab) return;
    selectTab(phaseTab);
    setOpenSection(null);
  }, [phaseTab, selectTab]);

  // Drop live gear rows whenever the workspace data is refetched so the banner
  // falls back to fresh server state until GearListSection re-emits on mount.
  useEffect(() => {
    setLiveGearItems(null);
  }, [ws.data]);

  useEffect(() => {
    if (!scrollTarget) return;
    if (openSection !== scrollTarget) return;
    const ref = sectionRefs[scrollTarget];
    const el = ref.current;
    if (!el) return;
    // The RMS's one sanctioned scroll motion (DESIGN.md, Motion): the
    // section glides into place so the Operator sees where it went; under
    // reduced motion it is simply there.
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    setScrollTarget(null);
  }, [scrollTarget, openSection, sectionRefs]);

  const handleToggle = useCallback((section: SectionKey): void => {
    setOpenSection((prev) => (prev === section ? null : section));
  }, []);

  const handleSelectTab = useCallback(
    (tab: PhaseTabKey): void => {
      selectTab(tab);
      setOpenSection(null);
    },
    [selectTab],
  );

  /** Open one section, on its own stage — the jump every "go fix this" does. */
  const goToSection = useCallback(
    (section: SectionKey): void => {
      selectTab(phaseTabForSection(section));
      setOpenSection(section);
      setScrollTarget(section);
    },
    [selectTab],
  );

  const handleQuoteGenerated = useCallback(async (): Promise<void> => {
    await ws.handleQuoteGenerated({
      onSuccess: () => goToSection("quote"),
    });
  }, [ws, goToSection]);

  const handleGenerateJobSummary = useCallback(async (): Promise<void> => {
    await ws.handleGenerateJobSummary({
      onSuccess: () => goToSection("jobSummary"),
    });
  }, [ws, goToSection]);

  // Jump to the Gear List section — that's where the "Re-generate Quote
  // Summary" button lives (not the Quote Summary section). Regenerating from
  // there re-renders the invoice drafts and clears the staleness badge.
  const handleGoToQuote = useCallback((): void => {
    goToSection("gearList");
  }, [goToSection]);

  // Jump to the Job Details section — where the "Re-generate Job Summary"
  // button lives (the stale cue sits in the Job Summary section below it).
  const handleGoToJobDetails = useCallback((): void => {
    goToSection("jobDetails");
  }, [goToSection]);

  if (ws.loading) return null;
  if (ws.error || !ws.data || !view) {
    return (
      <div className={page.page}>
        <div className={page.content}>
          <div className={styles.notFoundActions}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate("/events")}
            >
              Back to Events
            </Button>
          </div>
          <div className={styles.notFound}>
            <p className={styles.notFoundMessage}>
              {ws.error || "Event not found"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const {
    booking,
    assignments,
    quoteLineItems,
    gearItems,
    allContractors,
    approval,
    paymentSummary,
    documents,
    daySchedules,
    rawClientInput,
  } = ws.data;
  const quotePackages = ws.data.quotePackages ?? [];

  const { phase, step: currentStep, sections, depositDone, balanceDone } = view;
  const activeStatusOverridePhase =
    booking.status_override_phase &&
    booking.status_override_base_phase === (booking.derived_phase ?? phase)
      ? booking.status_override_phase
      : null;

  const notifiedOrAcceptedCount = assignments.filter(
    (a) => a.status === "notified" || a.status === "accepted",
  ).length;

  const generatingQuote = ws.mutations.isPending(MUTATION_KEYS.generateQuote);
  const generatingJobSummary = ws.mutations.isPending(
    MUTATION_KEYS.generateJobSummary,
  );
  const isApproved = !!approval?.client_approved_at;
  const crewToken =
    booking.contractor_selection_token || booking.approval_token;
  // Server-owned Labour Hours pool (#242 sweep 2 C4): internal/labour folds
  // the same service rows this payload ships (total → CrewSection's hours
  // prefill, per-category → the Job Details schedule seed). The server also
  // applies the package-ambiguity rule — null while multiple packages are
  // candidates pre-approval.
  const quotedLabourHours = ws.data.quotedLabour?.total_hours ?? null;
  const quotedLabour = ws.data.quotedLabour?.by_category ?? null;
  const activeCrew = assignments.filter(
    (a): a is AssignmentWithContractor => !!a.contractor && isActive(a),
  );
  const jobsheets = activeCrew.filter((a) => a.jobsheetUrl);
  const refresh = async () => {
    await ws.fetchData();
  };
  // 409 from the gear PUT (#242 C2): another writer replaced booking_items
  // since the editor was seeded. Refetch + remount so the editor re-seeds
  // from the winning state, and surface the notice above it.
  const handleGearSaveConflict = async () => {
    setGearConflictNotice(true);
    await refresh();
    setGearConflictKey((v) => v + 1);
  };

  const phaseTabs = [
    {
      key: "quoting" as const,
      label: "Quoting",
      isActive: currentStep <= 1,
      isDone: !!approval?.client_approved_at,
    },
    {
      key: "jobPrep" as const,
      label: "Job Prep",
      isActive: currentStep >= 2 && currentStep <= 4,
      isDone: currentStep > 4,
    },
    {
      key: "payments" as const,
      label: "Payments",
      isActive: currentStep >= 5,
      isDone: depositDone && balanceDone,
    },
  ];

  return (
    <div className={page.page}>
      <div className={page.content}>
        <EventHeader
          eventName={booking.event_name}
          quoteNumber={booking.quote_number}
          assignments={assignments}
          phase={phase}
          displayPhase={view.displayPhase}
          statusOverridePhase={activeStatusOverridePhase}
          busy={ws.busy}
          savingHeaderFields={ws.savingHeaderFields}
          savingStatusOverride={ws.mutations.isPending(
            MUTATION_KEYS.saveStatusOverride,
          )}
          onSaveField={ws.saveHeaderField}
          onSaveStatusOverride={ws.saveStatusOverride}
          onCancelClick={() => ws.setCancelConfirmOpen(true)}
          onBackClick={() => navigate("/events")}
        />

        {/* Ephemeral dismiss: clears local mutation-error state only. */}
        {ws.mutations.error && (
          <Banner
            variant="error"
            className={styles.banner}
            onDismiss={() => ws.mutations.setError(null)}
          >
            {ws.mutations.error}
          </Banner>
        )}

        <EventInfoCards
          booking={booking}
          inquiryDocuments={documents.inquiryDocuments ?? []}
          additionalInfo={rawClientInput?.additionalInfo ?? null}
          savingHeaderFields={ws.savingHeaderFields}
          savingHeaderNotes={ws.mutations.isPending(
            MUTATION_KEYS.saveHeaderNotes,
          )}
          onSaveField={ws.saveHeaderField}
          onSaveHeaderNotes={ws.saveHeaderNotes}
        />

        {/* Phone-call change proposals (#312): renders only while a call
            has undecided proposals (or an ambiguous call names this
            Booking); accepting refetches the workspace. */}
        {!booking.cancelled_at && (
          <CallProposalsSection bookingId={booking.id} onApplied={refresh} />
        )}

        {booking.cancelled_at && (
          <Banner variant="error" className={styles.banner}>
            <strong>
              This event was cancelled on {formatDateTime(booking.cancelled_at)}
              .
            </strong>{" "}
            All actions and edits below are disabled.
          </Banner>
        )}

        <div
          className={cx(
            styles.sections,
            booking.cancelled_at && styles.sectionsDisabled,
          )}
        >
          {/* Hired-in gear (PR2) — informational list of gear that must be
              sourced externally + the suppliers who can provide it. Driven by
              the live gear editor when available (so a manually-added hired-in
              item shows up before save), falling back to saved server state. */}
          <HiredInBanner gearItems={liveGearItems ?? gearItems} />

          <WorkspacePhaseTabs
            tabs={phaseTabs}
            current={currentTab}
            onSelect={handleSelectTab}
          />

          <div className={styles.stagePanel}>
            {currentTab === "quoting" && (
              <>
                <GearListSection
                  key={`gear-conflict-${gearConflictKey}`}
                  bookingId={booking.id}
                  gearItems={gearItems}
                  savables={ws.savables}
                  quotePackages={quotePackages}
                  isApproved={isApproved}
                  state={sections.lineItems.state}
                  onQuoteGenerated={handleQuoteGenerated}
                  onRefresh={refresh}
                  mutations={ws.mutations}
                  open={openSection === "gearList"}
                  onToggle={() => handleToggle("gearList")}
                  sectionRef={sectionRefs.gearList}
                  initialDepositPercent={
                    approval?.deposit_percent != null
                      ? String(approval.deposit_percent)
                      : quoteDefaults.depositPercent
                  }
                  initialQuoteValidDays={
                    approval?.quote_valid_days?.toString() ||
                    quoteDefaults.validDays
                  }
                  initialQuoteTemplateId={booking.quote_template_id ?? null}
                  initialNotes={approval?.quote_notes || ""}
                  initialGearDiscountPercent={
                    approval?.discount?.type === "percent" &&
                    typeof approval.discount.value === "number"
                      ? String(approval.discount.value)
                      : "0"
                  }
                  eventDayCount={getEventDayCount(
                    booking.event_date,
                    booking.event_end_date,
                  )}
                  eventDate={booking.event_date}
                  eventEndDate={booking.event_end_date}
                  multiDayExtraDayPercent={ws.data.multiDayExtraDayPercent}
                  quoteGeneratedAt={booking.quote_summary_generated_at || null}
                  gearShortfalls={booking.gear_shortfalls ?? null}
                  gearConflictAt={booking.gear_conflict_at ?? null}
                  dirtySections={dirtySections}
                  onGearItemsChange={handleGearItemsChange}
                  labourCategories={ws.data.quotedLabour?.categories ?? []}
                  itemsRevision={booking.items_revision ?? null}
                  onSaveConflict={handleGearSaveConflict}
                  conflictNotice={gearConflictNotice}
                  onDismissConflictNotice={() => setGearConflictNotice(false)}
                />

                <QuoteSection
                  bookingId={booking.id}
                  quoteNumber={booking.quote_number || ""}
                  lineItems={quoteLineItems}
                  quotePackages={quotePackages}
                  quoteSentAt={approval?.sent_to_client_at || null}
                  quoteGeneratedAt={booking.quote_summary_generated_at || null}
                  quoteReminderSentAt={approval?.quote_reminder_sent_at || null}
                  isApproved={isApproved}
                  approvedSignerName={approval?.signer_name || null}
                  clientApprovedAt={approval?.client_approved_at || null}
                  state={sections.quoteSummary.state}
                  // Content drift (snapshot projection / dirty section) OR
                  // section drift (the sent quote's sections snapshot no
                  // longer matches the resolved template).
                  isStale={
                    sections.quoteSummary.stale ||
                    Boolean(booking.quote_template_stale)
                  }
                  mutations={ws.mutations}
                  depositPercent={
                    approval?.deposit_percent != null
                      ? String(approval.deposit_percent)
                      : quoteDefaults.depositPercent
                  }
                  quoteValidDays={
                    approval?.quote_valid_days?.toString() ||
                    quoteDefaults.validDays
                  }
                  notes={approval?.quote_notes || ""}
                  open={openSection === "quote"}
                  onToggle={() => handleToggle("quote")}
                  sectionRef={sectionRefs.quote}
                  headerAction={
                    quotePackages.length === 0 &&
                    quoteLineItems.length > 0 &&
                    !generatingQuote &&
                    documents.quotePdf && (
                      <Button
                        variant="link"
                        size="sm"
                        href={documents.quotePdf}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Quote PDF
                      </Button>
                    )
                  }
                />
              </>
            )}

            {currentTab === "jobPrep" && (
              <>
                <JobDetailsSection
                  bookingId={booking.id}
                  callTime={booking.call_time || ""}
                  packOutTime={booking.pack_out_time || ""}
                  siteAvailableFrom={booking.site_available_from || ""}
                  callOutNotes={booking.call_out_notes || ""}
                  eventDate={booking.event_date}
                  eventEndDate={booking.event_end_date}
                  eventTime={booking.event_time}
                  daySchedules={daySchedules}
                  vehicleDayTimes={vehicleDayTimes}
                  quotedLabour={quotedLabour}
                  state={sections.jobDetails.state}
                  open={openSection === "jobDetails"}
                  onToggle={() => handleToggle("jobDetails")}
                  sectionRef={sectionRefs.jobDetails}
                  onGenerateJobSummary={handleGenerateJobSummary}
                  onRefresh={refresh}
                  contractorsNotifiedAt={
                    booking.contractors_notified_at || null
                  }
                  generatingJobSummary={generatingJobSummary}
                  jobSummaryGeneratedAt={
                    booking.job_summary_generated_at || null
                  }
                  assignmentCount={ws.localCrewCount ?? assignments.length}
                  savables={ws.savables}
                  dirtySections={dirtySections}
                >
                  {/* Vehicles before Crew: a crew row picks the vehicle its
                      driver takes, so the fleet has to be planned first. */}
                  <div className={styles.subBlock}>
                    <h3 className={styles.subHeading}>
                      Vehicles
                    </h3>
                    <VehiclePlanBlock
                      bookingId={booking.id}
                      vehicleConflicts={allocationConflicts.vehicles}
                      onRefresh={async () => {
                        await Promise.all([
                          refresh(),
                          vehicleDayTimes.syncAfterVehicleChange(),
                        ]);
                      }}
                    />
                  </div>

                  <div className={styles.subBlock}>
                    <h3 className={styles.subHeading}>
                      Crew
                    </h3>
                    <CrewSection
                      bookingId={booking.id}
                      token={crewToken}
                      assignments={assignments}
                      allContractors={allContractors}
                      quotedLabourHours={quotedLabourHours}
                      contractorConflicts={allocationConflicts.contractors}
                      savables={ws.savables}
                      onCrewCountChange={ws.setLocalCrewCount}
                      mutations={ws.mutations}
                    />
                  </div>
                </JobDetailsSection>

                <JobSummarySection
                  bookingId={booking.id}
                  token={crewToken}
                  booking={{
                    event_name: booking.event_name,
                    event_date: booking.event_date,
                    event_end_date: booking.event_end_date,
                    event_time: booking.event_time,
                    quote_number: booking.quote_number,
                    location: booking.location,
                    call_time: booking.call_time,
                    pack_out_time: booking.pack_out_time,
                    site_available_from: booking.site_available_from,
                    call_out_notes: booking.call_out_notes,
                    crew_count: booking.crew_count,
                    venue_contact: booking.venue_contact,
                    client_name: booking.client_name,
                    client_phone: booking.client_phone,
                    client_email: booking.client_email,
                    event_type: booking.event_type,
                    attendance: booking.attendance,
                    indoor_outdoor: booking.indoor_outdoor,
                    content_requirements: booking.content_requirements,
                    has_stage: booking.has_stage,
                    stage_details: booking.stage_details,
                    power_access: booking.power_access,
                    needs_generator: booking.needs_generator,
                    wet_weather_plan: booking.wet_weather_plan,
                    execution_notes: booking.execution_notes,
                  }}
                  gearItems={gearItems}
                  assignments={assignments}
                  contractorsNotifiedAt={
                    booking.contractors_notified_at || null
                  }
                  jobSummaryGeneratedAt={
                    booking.job_summary_generated_at || null
                  }
                  state={sections.jobSummary.state}
                  isStale={sections.jobSummary.stale}
                  onGoToJobDetails={handleGoToJobDetails}
                  mutations={ws.mutations}
                  open={openSection === "jobSummary"}
                  onToggle={() => handleToggle("jobSummary")}
                  sectionRef={sectionRefs.jobSummary}
                  headerAction={
                    !generatingJobSummary &&
                    jobsheets.length > 0 && (
                      <span className={styles.jobsheetLinks}>
                        {jobsheets.map((a) => (
                          <Button
                            key={a.id}
                            variant="link"
                            size="sm"
                            href={a.jobsheetUrl!}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {a.contractor.name.split(" ")[0]} Job Sheet PDF
                          </Button>
                        ))}
                      </span>
                    )
                  }
                />
              </>
            )}

            {currentTab === "payments" && (
              <>
                <ClientPaymentSection
                  approval={approval}
                  paymentSummary={paymentSummary}
                  hasLineItems={quoteLineItems.length > 0}
                  open={openSection === "clientPayment"}
                  onToggle={() => handleToggle("clientPayment")}
                  sectionRef={sectionRefs.clientPayment}
                  state={sections.clientPayment.state}
                  busy={ws.busy}
                  mutations={ws.mutations}
                  onSendDepositReminder={ws.handleSendDepositReminder}
                  onSendBalanceInvoice={ws.handleSendBalanceInvoice}
                  onSendBalanceReminder={ws.handleSendBalanceReminder}
                  onConfirmDeposit={(date) =>
                    ws.handleConfirmClientPayment("deposit", date)
                  }
                  onConfirmBalance={(date) =>
                    ws.handleConfirmClientPayment("balance", date)
                  }
                  depositInvoiceUrl={
                    generatingQuote ? null : documents.depositInvoice
                  }
                  balanceInvoiceUrl={
                    generatingQuote ? null : documents.balanceInvoice
                  }
                  // Content drift (snapshot projection / dirty section) OR
                  // invoice section drift (the sent invoices' snapshot no
                  // longer matches the resolved template) — both resolve via
                  // the same regenerate flow.
                  invoicesStale={
                    sections.quoteSummary.stale ||
                    Boolean(booking.invoice_template_stale)
                  }
                  balanceInvoiceNeedsReissue={view.balanceInvoiceNeedsReissue}
                  onRegenerate={handleGoToQuote}
                />

                <ContractorPaymentSection
                  assignments={assignments}
                  open={openSection === "contractorPayment"}
                  onToggle={() => handleToggle("contractorPayment")}
                  sectionRef={sectionRefs.contractorPayment}
                  state={sections.contractorPayment.state}
                  busy={ws.busy}
                  mutations={ws.mutations}
                  onConfirmPayment={ws.handleConfirmPayment}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={ws.cancelConfirmOpen}
        title="Cancel Event"
        message={
          <>
            This will cancel{" "}
            <strong>{booking.event_name || "this event"}</strong> and all
            contractor assignments.
          </>
        }
        warning={`The client${
          notifiedOrAcceptedCount > 0
            ? ` and ${notifiedOrAcceptedCount} contractor${notifiedOrAcceptedCount === 1 ? "" : "s"}`
            : ""
        } will be notified by email.`}
        confirmLabel="Cancel Event"
        confirmVariant="danger"
        loading={ws.mutations.isPending(MUTATION_KEYS.cancelEvent)}
        onConfirm={ws.handleCancelEvent}
        onCancel={() => ws.setCancelConfirmOpen(false)}
      />
    </div>
  );
}
